import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import { CommonEnums } from 'dashboard-services/api/common';
import Query from 'dashboard-services/api/Query'
import { DataStorageEnums } from 'dashboard-services/api/dataStorage';
import { DataTransformEnums } from 'dashboard-services/api/dataTransform';
import { StatusesEnums } from 'dashboard-services/api/statuses';
import { CommonService } from 'dashboard-services/services';
import TimeService from 'dashboard-services/services/TimeService';
import FormulaResolver from 'ng-formula-resolver/resolver'
import { loginActions, statusBoxActions } from 'primary-components';

import { basicCustomInsightConsts, inputParamsCustomInsightActions } from 'actions/modules/insight';
import { insightQueries } from 'queries/modules/insight';
import { routing } from 'routing';
import { customInsightSelectors } from 'selectors/modules/insight/custom';

import DatePresetRangeService from 'components/modules/Insight/CustomInsight/ChartInsight/InputParamsPanel/Presets/services/DatePresetRangeService'

const { useFromCustomState, useFromInsightTile, useFromInsightContent, useFromInsightTileObject, useFromInputParams, useFromTileOverwrite } = customInsightSelectors
const EMPTY_ARRAY = [];
export default (() => {
  const useOnError = ({ fileName = undefined, fileGroupName = undefined, title }) => {
    const dispatch = useDispatch(),
          titleRef = useRef(title)
    
    useEffect(() => {
      titleRef.current = title
    }, [title])

    const onFileNotFound =  useCallback(() => {
      dispatch(statusBoxActions.addItem({
        message: `File ${fileName} not found in group ${fileGroupName}`,
        title: titleRef.current,
        variant: StatusesEnums.STATUS.ERROR,
      }))
    }, [dispatch, fileName, fileGroupName])

    const onFileParsingError = useCallback(() => {
      dispatch(statusBoxActions.addItem({
        message: "CSV file can not be parsed.",
        title: titleRef.current,
        variant: StatusesEnums.STATUS.ERROR,
      }))
    }, [dispatch])

    return fileName && fileGroupName ? onFileNotFound : onFileParsingError
  }

  const useHasBasketSource = ({ source = undefined }) => {
    const basket = useFromCustomState("basket"),
          hasBasketSource = useMemo(() => Object.keys(basket).filter(basketId => basketId === source?.value?.id).length > 0, [basket, source])
    return hasBasketSource
  }

  const useGetInsightCurves = ({ getQueryBuilder, keysConfig, tileIndex, passLabelDateFormatToCall = false, defaultCsvHeadersToUse }) => {
    const symbolSize = useFromInsightTile({ tileIndex, paramName: "symbolSize" }),
          fillBlanks = useFromInsightTile({ tileIndex, paramName: "fillBlanks" }),
          isRelative = useFromInsightTile({ tileIndex, paramName: "isRelative", defaultValue: "false" }),
          periodType = useFromInsightTile({ tileIndex, paramName: "periodType", defaultValue: DataTransformEnums.PERIOD_TYPE.MONTH }),
          range = useFromInsightTile({ tileIndex, paramName: "range", defaultValue: DataStorageEnums.DATA_RANGE.LAST }),
          lastType = useFromInsightTile({ tileIndex, paramName: "lastType", defaultValue: DataStorageEnums.LAST_TYPE.VALUE }),
          lastTypeAmount = useFromInsightTile({ tileIndex, paramName: "lastTypeAmount", defaultValue: "1" }),
          timeZone = useFromInsightTile({ tileIndex, paramName: "timeZone", defaultValue: TimeService.UTC }),
          contractSort = useFromInsightTile({ tileIndex, paramName: "contractSort", defaultValue: CommonEnums.SORT_ORDER.ASCENDING }),
          csvHeaders = useFromInsightTile({ tileIndex, paramName: "csvHeaders", defaultValue: defaultCsvHeadersToUse }),
          labelDateFormat = useFromInsightTile({ tileIndex, paramName: "labelDateFormat", defaultValue: "yyyy-MM-dd" }),
          from = useFromInsightTile({ tileIndex, paramName: "from" }),
          fromSelected = useFromInsightTile({ tileIndex, paramName: "fromSelected" }),
          to = useFromInsightTile({ tileIndex, paramName: "to" }),
          toSelected = useFromInsightTile({ tileIndex, paramName: "toSelected" }),
          specificDate = useFromInsightTile({ tileIndex, paramName: "specificDate" }),
          keys = useMemo(() => inputParamsCustomInsightActions.getCurveBodyKeys({ items: keysConfig }), [keysConfig]),
          requestBody = useMemo(() => inputParamsCustomInsightActions.getCurveBody({ keys, passLabelDateFormatToCall, labelDateFormat, contractSort, fillBlanks, periodType, symbolSize, isRelative, timeZone, csvHeaders, range, from, to, specificDate, fromSelected, toSelected, lastType, lastTypeAmount }), [keys, contractSort, passLabelDateFormatToCall, labelDateFormat, fillBlanks, periodType, symbolSize, isRelative, timeZone, csvHeaders, range, fromSelected, from, toSelected, to, specificDate, lastType, lastTypeAmount]),
          { mutateAsync: getCurvesDataCall } = insightQueries.useGetCurvesDataMutation({ 
            getQueryBuilder
          }),
          areThereAnyKeys = requestBody?.keys?.length > 0,
          toReturn = useMemo(() => ({ getCurvesDataCall, areThereAnyKeys, requestBody }), [areThereAnyKeys, getCurvesDataCall, requestBody])

    return toReturn;
  }

  const useDebouncedScaleChange = ({ onChange, options = {} } = {}) => {
    const { delay = 500, min = 25, max = 300 } = options,
          timeoutRef = useRef(),
          onScaleChange = useCallback((value, { name }) => {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current)
            }
  
            timeoutRef.current = setTimeout(() => {
              if (CommonService.isNumber(value)) {
                const n = Number(value)
                if (n >= min && n <= max) onChange(value, { name })
              } else if (value === "") {
                onChange(undefined, { name })
              }
            }, delay)
          },[onChange, delay, min, max])
  
    useEffect(() => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }, [])
  
    return { onScaleChange }
  }

  const useIsSingleTile = (() => {
    const pathname = useLocation()
    const dispatch = useDispatch(),
          tileName = useFromCustomState("tileName"),
          rawTiles = useFromInsightContent("tiles") || EMPTY_ARRAY,
          isPublicDirectory = useMemo(() => String(pathname).startsWith(`/${routing.public.name}`), [pathname]),
          hasWorkflowReadAccess = useMemo(() => dispatch(loginActions.hasAccessTo(routing.insight.routing.workflow.name, "R", routing.insight.name)), [dispatch]),
          hasVisualisationReadAccess = useMemo(() => dispatch(loginActions.hasAccessTo(routing.insight.routing.visualisation.name, "R", routing.insight.name)), [dispatch]),
          isIframe = useMemo(() => window !== window.parent, []),
          tileNameTrimmed = useMemo(() => tileName ? String(tileName).trim() : tileName, [tileName]),
          singleTileIndex = useMemo(() => rawTiles?.findIndex(t => String(t.title).trim() === tileNameTrimmed), [rawTiles, tileNameTrimmed]),
          singleTile = useMemo(() => rawTiles?.[singleTileIndex], [rawTiles, singleTileIndex]),
          hasAccessToSingleTile = useMemo(() => {
            if((hasVisualisationReadAccess || isPublicDirectory) && basicCustomInsightConsts.VISUALISATION_TILES.includes(singleTile?.type)) {
              return true;
            } else if(hasWorkflowReadAccess && !isPublicDirectory && basicCustomInsightConsts.WORKFLOW_TILES.includes(singleTile?.type) && !isIframe) {
              return true;
            }
            return false;
          }, [hasVisualisationReadAccess, hasWorkflowReadAccess, isIframe, isPublicDirectory, singleTile?.type])

    return singleTile && hasAccessToSingleTile
  })

  const useIsSingleTileView = () => {
    const { tileName } = useParams()
    return tileName !== undefined
  }

  const useSelectedVersionId = ({ tileIndex }) => {
    const [searchParams] = useSearchParams(),
          fileIdFromUrl = searchParams.get("fileId") || undefined,
          isSingleTile = useIsSingleTileView(),
          currFileId = useFromTileOverwrite({ tileIndex, paramName: "currFileId" })
  
    return isSingleTile && fileIdFromUrl ? fileIdFromUrl : currFileId
  }

  const useWithSubtitle = ({ tileIndex }) => {
    const source = useFromInsightTile({ tileIndex, paramName: "source" }),
          sourceType = source?.type,
          sourceInputParamsId = sourceType === basicCustomInsightConsts.TILE_SOURCE_TYPES.SOURCE_TILE ? source?.value?.id : undefined,
          showInputParamsOnTile = useFromInsightTile({ tileIndex, paramName: "showInputParamsOnTile" }),
          showSourceFileVersions = useFromInsightTile({ tileIndex, paramName: "showSourceFileVersions" }),
          withOptionalSave = useFromInsightTileObject({ tileIndex, paramName: "formulaPanel", fieldName: "withOptionalSave", defaultValue: false }),
          isSingleTile = useIsSingleTileView(),
          doesInputParamsExist = !!useFromInputParams({ id: sourceInputParamsId, paramName: "inputParams" })

    return useMemo(() => {
      const withInputParams = showInputParamsOnTile && doesInputParamsExist && !isSingleTile,
            withFormulaPanel = withOptionalSave && !isSingleTile,
            withSourceVersions = showSourceFileVersions && !isSingleTile

      return withInputParams || withFormulaPanel || withSourceVersions
    }, [doesInputParamsExist, isSingleTile, showInputParamsOnTile, showSourceFileVersions, withOptionalSave])
  }

  // CodeMirror 5 only re-measures on window resize, so refresh it when its container changes size
  const useRefreshCodeMirrorOnResize = ({ elementRef, getEditor }) => {
    useEffect(() => {
      const element = elementRef.current
      if(element && typeof ResizeObserver !== "undefined") {
        const frame = { id: undefined },
              observer = new ResizeObserver(() => {
                cancelAnimationFrame(frame.id)
                frame.id = requestAnimationFrame(() => getEditor()?.refresh?.())
              })
        observer.observe(element)
        return () => {
          observer.disconnect()
          cancelAnimationFrame(frame.id)
        }
      }
    }, [elementRef, getEditor])
  }

  const usePresetDateRange = () => {
    const dispatch = useDispatch(),
          constants = useSelector(state => state.myAccountState.settingsState.formulaPanelConstants) || EMPTY_ARRAY,
          parserState = useMemo(() => {
            try {
              const customConstants = Object.fromEntries(constants.filter(item => item?.name).map(item => [item.name, item.value]))
              return { parser: FormulaResolver.createFormulaParser({ customConstants }) }
            } catch(error) {
              return { parserError: error }
            }
          }, [constants]),
          getQueryBuilder = useCallback(() => {
            // Loading ApiConfig here avoids a cycle through account settings during hook module initialization.
            const { getThrottledApiConfig } = require('api/ApiConfig')
            return new Query.Builder(dispatch(getThrottledApiConfig())).withOnParse(response => Promise.resolve(response)).withFetching(false)
          }, [dispatch])

    return useCallback(({ preset, definedTime, dataRange }) => DatePresetRangeService.getPresetDateRange({ preset, definedTime, dataRange, getQueryBuilder, ...parserState }), [getQueryBuilder, parserState])
  }

  /** @deprecated use one from consts instead */
  const getParamLabel = basicCustomInsightConsts.getParamLabel
  /** @deprecated use one from consts instead */
  const getParamDefaultValue = basicCustomInsightConsts.getParamDefaultValue
  /** @deprecated use one from consts instead */
  const getParamMapper = basicCustomInsightConsts.getParamMapper
  /** @deprecated use one from consts instead */
  const getSection = basicCustomInsightConsts.getSection

  return {
    useOnError,
    useHasBasketSource,
    useGetInsightCurves,
    useDebouncedScaleChange,
    useIsSingleTile,
    useIsSingleTileView,
    useSelectedVersionId,
    useWithSubtitle,
    useRefreshCodeMirrorOnResize,
    usePresetDateRange,
    getParamLabel,
    getParamDefaultValue,
    getParamMapper,
    getSection
  }
})()
